import * as Types from "../constants/ReportDashboard";
import { Types as ProfileTypes } from "../constants/actionTypes";

const defaultSelectedFilters = {
  customerIds: [],
  locationIds: [],
  providerIds: [],
  therapistId: null,
  accountType: "ALL",
  timeframe: "CURRENT",
  startDate: null,
  endDate: null,
  month: null,
};

const filterListState = {
  loading: false,
  loadingMore: false,
  error: null,
  list: [],
  pageNo: 0,
  totalPage: 0,
  totalRecords: 0,
  hasMore: false,
};

const mergeFilterPageSuccess = (current, payload) => {
  const { list, pageNo, totalPage, totalRecords, hasMore, append } = payload;
  return {
    loading: false,
    loadingMore: false,
    error: null,
    list: append ? [...current.list, ...list] : list,
    pageNo,
    totalPage,
    totalRecords,
    hasMore,
  };
};

const initialState = {
  activeSection: "dashboard",
  dataOwnerTherapistId: null,
  filters: {
    customers: { ...filterListState },
    locations: { ...filterListState },
    providers: { ...filterListState },
    adherenceThresholds: { lowPercentage: 50, highPercentage: 80 },
  },
  selectedFilters: { ...defaultSelectedFilters },
  filtersSnapshotBeforeRtm: null,
  totalRtmClaims: { loading: false, error: null, data: null },
  providerTime: { loading: false, error: null, data: null },
  patientEngagement: { loading: false, thresholdOnly: false, error: null, data: null },
  patientEngagementThresholds: { min: null, max: null },
  adherenceTrend: { loading: false, error: null, data: null },
  rtmClaims: { loading: false, error: null, data: null },
  rtmClaimDetails: {
    eligibleOnly: true,
    loading: false,
    loadingMore: false,
    error: null,
    list: [],
    pageNo: 0,
    totalPage: 0,
    totalRecords: 0,
    hasMore: false,
  },
};

const normalizeFilterList = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value) && Array.isArray(value.list)) {
    return {
      loading: !!value.loading,
      loadingMore: !!value.loadingMore,
      error: value.error ?? null,
      list: value.list,
      pageNo: value.pageNo ?? 0,
      totalPage: value.totalPage ?? 0,
      totalRecords: value.totalRecords ?? value.list.length,
      hasMore: !!value.hasMore,
    };
  }
  if (Array.isArray(value)) {
    return { ...filterListState, list: value };
  }
  return { ...filterListState };
};

const normalizeSelectedFilters = (selectedFilters = {}) => {
  const customerIds = selectedFilters.customerIds
    ?? (selectedFilters.customerId != null ? [selectedFilters.customerId] : []);

  return {
    ...defaultSelectedFilters,
    ...selectedFilters,
    customerIds: Array.isArray(customerIds) ? customerIds : [],
    locationIds: Array.isArray(selectedFilters.locationIds) ? selectedFilters.locationIds : [],
    providerIds: Array.isArray(selectedFilters.providerIds) ? selectedFilters.providerIds : [],
  };
};

const normalizeState = (state) => {
  if (!state) return initialState;

  const filters = state.filters ?? {};

  return {
    ...initialState,
    ...state,
    dataOwnerTherapistId: state.dataOwnerTherapistId ?? null,
    filters: {
      customers: normalizeFilterList(filters.customers),
      locations: normalizeFilterList(filters.locations),
      providers: normalizeFilterList(filters.providers),
      adherenceThresholds: filters.adherenceThresholds ?? initialState.filters.adherenceThresholds,
    },
    selectedFilters: normalizeSelectedFilters(state.selectedFilters),
    filtersSnapshotBeforeRtm: state.filtersSnapshotBeforeRtm ?? null,
    totalRtmClaims: state.totalRtmClaims ?? initialState.totalRtmClaims,
    providerTime: state.providerTime ?? initialState.providerTime,
    patientEngagement: state.patientEngagement ?? initialState.patientEngagement,
    patientEngagementThresholds: state.patientEngagementThresholds ?? initialState.patientEngagementThresholds,
    adherenceTrend: state.adherenceTrend ?? initialState.adherenceTrend,
    rtmClaims: state.rtmClaims ?? initialState.rtmClaims,
    rtmClaimDetails: {
      ...initialState.rtmClaimDetails,
      ...(state.rtmClaimDetails ?? {}),
      list: Array.isArray(state.rtmClaimDetails?.list) ? state.rtmClaimDetails.list : [],
    },
  };
};

const reportDashboard = (state = initialState, action) => {
  const s = normalizeState(state);

  switch (action.type) {
    case Types.FETCH_RD_FILTER_CUSTOMERS_REQUEST:
      return {
        ...s,
        filters: {
          ...s.filters,
          customers: {
            ...s.filters.customers,
            loading: !action.payload?.append,
            loadingMore: !!action.payload?.append,
            error: null,
          },
        },
      };
    case Types.FETCH_RD_FILTER_CUSTOMERS_SUCCESS:
      return {
        ...s,
        filters: {
          ...s.filters,
          customers: mergeFilterPageSuccess(s.filters.customers, action.payload),
        },
      };
    case Types.FETCH_RD_FILTER_CUSTOMERS_FAILURE:
      return {
        ...s,
        filters: {
          ...s.filters,
          customers: {
            ...s.filters.customers,
            loading: false,
            loadingMore: false,
            error: action.payload?.message ?? action.payload,
            list: action.payload?.append ? s.filters.customers.list : [],
          },
        },
      };

    case Types.FETCH_RD_FILTER_LOCATIONS_REQUEST:
      return {
        ...s,
        filters: {
          ...s.filters,
          locations: {
            ...s.filters.locations,
            loading: !action.payload?.append,
            loadingMore: !!action.payload?.append,
            error: null,
          },
        },
      };
    case Types.FETCH_RD_FILTER_LOCATIONS_SUCCESS:
      return {
        ...s,
        filters: {
          ...s.filters,
          locations: mergeFilterPageSuccess(s.filters.locations, action.payload),
        },
      };
    case Types.FETCH_RD_FILTER_LOCATIONS_FAILURE:
      return {
        ...s,
        filters: {
          ...s.filters,
          locations: {
            ...s.filters.locations,
            loading: false,
            loadingMore: false,
            error: action.payload?.message ?? action.payload,
            list: action.payload?.append ? s.filters.locations.list : [],
          },
        },
      };

    case Types.FETCH_RD_FILTER_PROVIDERS_REQUEST:
      return {
        ...s,
        filters: {
          ...s.filters,
          providers: {
            ...s.filters.providers,
            loading: !action.payload?.append,
            loadingMore: !!action.payload?.append,
            error: null,
          },
        },
      };
    case Types.FETCH_RD_FILTER_PROVIDERS_SUCCESS:
      return {
        ...s,
        filters: {
          ...s.filters,
          providers: mergeFilterPageSuccess(s.filters.providers, action.payload),
        },
      };
    case Types.FETCH_RD_FILTER_PROVIDERS_FAILURE:
      return {
        ...s,
        filters: {
          ...s.filters,
          providers: {
            ...s.filters.providers,
            loading: false,
            loadingMore: false,
            error: action.payload?.message ?? action.payload,
            list: action.payload?.append ? s.filters.providers.list : [],
          },
        },
      };

    case Types.FETCH_REPORT_DASHBOARD_REQUEST:
      return {
        ...s,
        totalRtmClaims: { loading: true, error: null, data: s.totalRtmClaims.data },
      };
    case Types.FETCH_REPORT_DASHBOARD_SUCCESS:
      return {
        ...s,
        totalRtmClaims: { loading: false, error: null, data: { totalRtmClaims: action.payload.totalRtmClaims } },
      };
    case Types.FETCH_REPORT_DASHBOARD_FAILURE:
      return {
        ...s,
        totalRtmClaims: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_PROVIDER_TIME_SUMMARY_REQUEST:
      return {
        ...s,
        providerTime: { loading: true, error: null, data: s.providerTime.data },
      };
    case Types.FETCH_PROVIDER_TIME_SUMMARY_SUCCESS:
      return {
        ...s,
        providerTime: {
          loading: false,
          error: null,
          data: { totalProviderTimeHours: action.payload.totalProviderTimeHours },
        },
      };
    case Types.FETCH_PROVIDER_TIME_SUMMARY_FAILURE:
      return {
        ...s,
        providerTime: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_PATIENT_ENGAGEMENT_REQUEST:
      return {
        ...s,
        patientEngagement: {
          loading: true,
          thresholdOnly: !!action.payload?.thresholdOnly,
          error: null,
          data: s.patientEngagement.data,
        },
      };
    case Types.FETCH_PATIENT_ENGAGEMENT_SUCCESS:
      return {
        ...s,
        patientEngagement: {
          loading: false,
          thresholdOnly: false,
          error: null,
          data: action.payload,
        },
        patientEngagementThresholds: {
          min: action.payload.minAdherenceThreshold != null
            ? action.payload.minAdherenceThreshold
            : s.patientEngagementThresholds.min,
          max: action.payload.maxAdherenceThreshold != null
            ? action.payload.maxAdherenceThreshold
            : s.patientEngagementThresholds.max,
        },
      };
    case Types.SET_PATIENT_ENGAGEMENT_THRESHOLDS:
      return {
        ...s,
        patientEngagementThresholds: {
          ...s.patientEngagementThresholds,
          ...action.payload,
        },
      };
    case Types.FETCH_PATIENT_ENGAGEMENT_FAILURE:
      return {
        ...s,
        patientEngagement: {
          loading: false,
          thresholdOnly: false,
          error: action.payload,
          data: null,
        },
      };

    case Types.FETCH_ADHERENCE_TREND_REQUEST:
      return { ...s, adherenceTrend: { loading: true, error: null, data: s.adherenceTrend.data } };
    case Types.FETCH_ADHERENCE_TREND_SUCCESS:
      return { ...s, adherenceTrend: { loading: false, error: null, data: action.payload } };
    case Types.FETCH_ADHERENCE_TREND_FAILURE:
      return { ...s, adherenceTrend: { loading: false, error: action.payload, data: null } };

    case Types.FETCH_RTM_CLAIMS_REQUEST:
      return { ...s, rtmClaims: { loading: true, error: null, data: s.rtmClaims.data } };
    case Types.FETCH_RTM_CLAIMS_SUCCESS:
      return { ...s, rtmClaims: { loading: false, error: null, data: action.payload } };
    case Types.FETCH_RTM_CLAIMS_FAILURE:
      return { ...s, rtmClaims: { loading: false, error: action.payload, data: null } };

    case Types.FETCH_RTM_CLAIM_DETAILS_REQUEST:
      return {
        ...s,
        rtmClaimDetails: {
          ...s.rtmClaimDetails,
          eligibleOnly: action.payload.eligibleOnly,
          loading: !action.payload.append,
          loadingMore: !!action.payload.append,
          error: null,
          ...(action.payload.append ? {} : { list: [] }),
        },
      };
    case Types.FETCH_RTM_CLAIM_DETAILS_SUCCESS:
      return {
        ...s,
        rtmClaimDetails: {
          ...s.rtmClaimDetails,
          ...mergeFilterPageSuccess(s.rtmClaimDetails, action.payload),
          eligibleOnly: action.payload.eligibleOnly ?? s.rtmClaimDetails.eligibleOnly,
        },
      };
    case Types.FETCH_RTM_CLAIM_DETAILS_FAILURE:
      return {
        ...s,
        rtmClaimDetails: {
          ...s.rtmClaimDetails,
          loading: false,
          loadingMore: false,
          error: action.payload?.message ?? action.payload,
          list: action.payload?.append ? s.rtmClaimDetails.list : [],
        },
      };

    case Types.SET_REPORT_DASHBOARD_ACTIVE_SECTION:
      return { ...s, activeSection: action.payload };

    case Types.SET_REPORT_DASHBOARD_FILTER:
      return {
        ...s,
        selectedFilters: { ...s.selectedFilters, [action.payload.key]: action.payload.value },
        patientEngagementThresholds: { min: null, max: null },
        adherenceTrend: { loading: false, error: null, data: null },
      };
    case Types.RESET_REPORT_DASHBOARD_FILTERS:
      return {
        ...s,
        selectedFilters: { ...defaultSelectedFilters },
        filtersSnapshotBeforeRtm: null,
        patientEngagementThresholds: { min: null, max: null },
        adherenceTrend: { loading: false, error: null, data: null },
      };

    case Types.SNAPSHOT_FILTERS_BEFORE_RTM:
      return { ...s, filtersSnapshotBeforeRtm: action.payload };

    case Types.RESTORE_FILTERS_FROM_RTM_SNAPSHOT:
      if (!s.filtersSnapshotBeforeRtm) return s;
      return {
        ...s,
        selectedFilters: {
          ...s.selectedFilters,
          ...s.filtersSnapshotBeforeRtm,
        },
        filtersSnapshotBeforeRtm: null,
        adherenceTrend: { loading: false, error: null, data: null },
      };

    case Types.RESET_REPORT_DASHBOARD:
      return {
        ...initialState,
        dataOwnerTherapistId: action.payload?.dataOwnerTherapistId ?? null,
      };

    case ProfileTypes.RESET_STORE:
    case ProfileTypes.LOGOUT:
      return initialState;

    case ProfileTypes.ADD_USER:
    case ProfileTypes.LOGIN: {
      const nextTherapistId = action.payload?.user?.id ?? null;
      if (!nextTherapistId) return s;
      if (s.dataOwnerTherapistId !== nextTherapistId) {
        return { ...initialState, dataOwnerTherapistId: nextTherapistId };
      }
      return s;
    }

    default:
      return s;
  }
};

export default reportDashboard;
